import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { Constants } from '@app/common/constants';
import { HttpHelper } from '@app/common/http-helper';
import { Restaurant } from '@app/models/restaurant-menu';
import { CacheService } from '@app/services/cache.service';
import { RestaurantMenuService } from '@app/services/restaurant-menu.service';
import { Environment } from '@environments/interface.environment';
import { BehaviorSubject, concat, Observable, of, Subject } from 'rxjs';
import { catchError, map, shareReplay, takeUntil, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class RestaurantService {

  constructor(@Inject(CUSTOMERENVIRONMENT) private environment: Environment,
              private http: HttpClient,
              private cache: CacheService,
              private restaurantMenuService: RestaurantMenuService,
              private httpHelper: HttpHelper) { }

  private readonly cacheKey = 'RestaurantService-getAllRestaurants';
  public useCache = true;
  private internalReload$ = new Subject<void>();
  public reload$ = new BehaviorSubject<void>(null);
  private getAllRestaurants$: Observable<Restaurant[]>;

  public reload() {
    this.getAllRestaurants$ = null;

    if (this.useCache) {
      this.cache.removeAllItemsInPersistentCacheWithGeneratedKeyPrefix(this.cacheKey);
    }

    this.restaurantMenuService.reload();
    this.internalReload$.next();
    this.reload$.next(null);
  }

  addRestaurant(restaurantMenu: Restaurant): Observable<Restaurant> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    return this.http
      .post<Restaurant>(`${Constants.API_ENDPOINT}/restaurants`, restaurantMenu, { params: httpParams })
      .pipe(
        tap(() => {
          this.reload();
        }),
        catchError(this.httpHelper.handleError({ operation: 'addRestaurant', result: null, rethrow: true })));
  }

  updateRestaurant(id: number, restaurantMenu: Restaurant): Observable<Restaurant> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    return this.http
      .put<Restaurant>(`${Constants.API_ENDPOINT}/restaurants/${id}`, restaurantMenu, { params: httpParams })
      .pipe(
        tap(() => {
          this.reload();
        }),
        catchError(this.httpHelper.handleError({ operation: 'updateRestaurant', result: null, rethrow: true })));
  }

  deleteRestaurant(id: number): Observable<Restaurant> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const fetchOperation = this.http.delete<Restaurant>(`${Constants.API_ENDPOINT}/restaurants/${id}`, {
      params: httpParams
    })
      .pipe(
        map(response => {
          console.log(`deleted result: `);
          console.log(response);
          this.reload();
          return response;
        }),
        catchError(this.httpHelper.handleError({ operation: 'deleteRestaurant', result: ({} as Restaurant) })));

    return fetchOperation;
  }

  getRestaurant(id: number): Observable<Restaurant> {
    return this.http
      .get<Restaurant>(`${Constants.API_ENDPOINT}/restaurants/${id}`)
      .pipe(
        catchError(this.httpHelper.handleError({ operation: 'getRestaurant', result: null, rethrow: true })));
  }

  getAllRestaurants(): Observable<Array<Restaurant>> {
    if (this.getAllRestaurants$) {
      return this.getAllRestaurants$;
    } else {
      this.getAllRestaurants$ = this.http
        .get<Array<Restaurant>>(`${Constants.API_ENDPOINT}/restaurants`)
        .pipe(
          tap(data => {
            this.useCache && this.cache.set(this.cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getAllRestaurants', result: new Array<Restaurant>(), rethrow: true, cacheKey: this.cacheKey })),
          takeUntil(this.internalReload$),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<Restaurant>>(this.cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getAllRestaurants$);
        }
      }
      return this.getAllRestaurants$;
    }
  }
}
